import { Field, useFormikContext } from 'formik';
import React, { FC, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { CERTIFICATE_IS_CA, CERTIFICATE_STATUS, CERTIFICATE_TYPES } from "../../constants/certificate-constants";
import { COMMAND_STATE } from "../../constants/command-status";
import Commands from "../../constants/commands";
import { AccountSubCmds } from "../../constants/sub-commands";
import { useLocale } from '../../contexts/localeContext';
import { TranslationKeys } from '../../contexts/TranslationKeys';
import { ButtonTypes } from "../../models/ButtonTypes";
import { ExecuteCommand } from "../../models/ExecuteCommand";
import { ListQuery } from '../../models/ListQuery';
import { executeCommand } from "../../services/executeCommandService";
import { getCertificateDetailsById, getCertificateNamesWithPagination } from '../../services/securityPolicyService';
import { verifyFileLocation } from '../../services/systemService';
import { getErrorMessage } from '../../services/utilities/ServiceError';
import { appendFilter } from '../../utilities/filters';
import { DialogContent } from "../dialog/Dialog";
import { useDialog } from '../dialog/DialogContext';
import Label from '../label/label';
import PaginatedSearchableSelectField from '../selection-field/paginated-searchable-select-field';
import Tooltip from "../Tooltip/Tooltip";
import ImportCrlFile from "./importFiles/ImportCrlModal";

/**
 * Capture the certificate details for connecting to the MQTT broker.
 * @param param0
 * @returns
 */
export const MqttServiceAccountCertificates: FC = () => {
    const { values, setFieldValue } = useFormikContext<any>();
    const { translate } = useLocale();
    const [brokerCert, setBrokerCert] = useState<string>('');
    let shouldRefreshLabel = true;
    const dialog = useDialog();
    const [busy, setBusy] = useState(false);
    const [crlOpenFile, setCrlOpenFile] = useState(false);

    useEffect(() => {
        if ((values.details.tlsBrokerCert.value) && shouldRefreshLabel) {
            setSelectedItemLabel();
        }
    }, [values.details.tlsBrokerCert.value]);

    const setSelectedItemLabel = async () => {
        const brokerCertName = (await getCertificateDetailsById(parseInt(values.details.tlsBrokerCert.value), translate)).data.name;
        setBrokerCert(brokerCertName);
    }

    /**
     * Allow the user to select the Broker service location.
     * @param event
     */
    const onVerifyBrokerLocation = (event) => {
        const filePath = values.details.tlsBrokerLocation.value;
        verifyFileLocation(filePath).then((result) => {
            toast.success(translate.byKey(TranslationKeys.brokerServiceLocationVerified));
        }).catch((error) => {
            dialog?.pushError({ title: translate.byKey(TranslationKeys.error), message: getErrorMessage(error) });
        });
    };

    const removeCrlFile = (event) => {
        const dialogContent: DialogContent = {
            title: translate.byKey(TranslationKeys.crlRemoveTitle),
            message: translate.byKey(TranslationKeys.crlWarningRemovingDescription),
            buttons: [
                {
                    id: "crlRemoveId",
                    text: translate.byKey(TranslationKeys.crlRemove),
                    onClick: () => {
                        removeRefreshCrlFile(AccountSubCmds.CRL_REMOVE, TranslationKeys.removeCrlCompleted, TranslationKeys.removeCrlFailed);
                    },
                    type: ButtonTypes.RedRoundedButton
                },
                {
                    id: "cancelCrlRemoveId",
                    text: translate.byKey(TranslationKeys.cancel),
                    onClick: () => {
                    },
                    type: ButtonTypes.TextButton
                }
            ]
        }
        dialog.pushInfo(dialogContent);
    }

    const checkForCRLRefresh = (event) => {
        const dialogContent: DialogContent = {
            title: translate.byKey(TranslationKeys.crlRefreshTitle),
            message: translate.byKey(TranslationKeys.crlWarningDescription),
            buttons: [
                {
                    id: "crlRefreshId",
                    text: translate.byKey(TranslationKeys.crlRefresh),
                    onClick: () => {
                        removeRefreshCrlFile(AccountSubCmds.CRL_REFRESH, TranslationKeys.refreshCrlCompleted, TranslationKeys.refreshCrlFailed);
                    },
                    type: ButtonTypes.BlueRoundedButton
                },
                {
                    id: "cancelCrlRefreshId",
                    text: translate.byKey(TranslationKeys.cancel),
                    onClick: () => {
                    },
                    type: ButtonTypes.TextButton
                }
            ]
        }
        dialog.pushInfo(dialogContent);
    }

    const removeRefreshCrlFile = (subType: string, successKey: string, failedKey: string) => {
        setBusy(true);
        const command: ExecuteCommand = {
            cmd: Commands.Account.label,
            cmdSubType: subType,
            jsonResponse: ""
        }
        executeCommand(command).then((response: any) => {
            if (response.status === COMMAND_STATE.COMMAND_COMPLETE) {
                toast.success(translate.byKey(successKey));
            }
            else {
                dialog.pushError({ title: translate.byKey(TranslationKeys.error), message: translate.byKey(failedKey) });
            }
        }).catch((error) => {
            dialog.pushError({ title: translate.byKey(TranslationKeys.error), message: getErrorMessage(error) });
        }).finally(() => {
            setBusy(false);
        });
    }

    return <div className='flex flex-col flex-grow bg-white'>
        <div className="flex flex-col mt-3">
            <Label htmlFor='brokerHeading' className="text-portal-blue-primary-shade2 text-md font-semibold">{translate.byKey(TranslationKeys.mqttBrokerConnectionSettings)}</Label>
            <span className="text-sm text-gray-500">{translate.byKey(TranslationKeys.mqttBrokerServiceLocationTip)}</span>
        </div>

        <div className="flex flex-col mt-3">
            <Label htmlFor='brokerCertificateType' className="text-xs text-gray-600 mt-1">{translate.byKey(TranslationKeys.brokerCertificate)}</Label>
            <div className="mt-1 w-64">
                <PaginatedSearchableSelectField
                    name={translate.byKey(TranslationKeys.brokerCertificate)}
                    key='brokerCertificateType'
                    accessor='details.tlsBrokerCert.value'
                    dataMethod={(listParameters?: ListQuery) => getCertificateNamesWithPagination({
                        ...listParameters,
                        filters: appendFilter(CERTIFICATE_IS_CA.NO, 'Certificate CA', 'certificateAuthority',
                            appendFilter(CERTIFICATE_TYPES.MQTT_BROKER, 'Type', 'ksCertificateType.id',
                                appendFilter(CERTIFICATE_STATUS.REVOKED, 'Certificate Status', 'ksCertificateStatus.id', listParameters?.filters ?? [], "=out=")))
                    })}
                    searchAccessor={'trnName'}
                    required={false} // Not required because the user may be running this from another machine we can't access.
                    selectedItemLabel={brokerCert}
                    onSelect={(_, value) => {
                        shouldRefreshLabel = false;
                        setFieldValue('details.tlsBrokerCert.certName', value?.label);
                        setFieldValue('details.tlsBrokerCert.value', value?.value);
                    }} />
            </div>
        </div>

        <div className="flex flex-row mt-2 ">
            <div className="flex flex-col" style={{ width: '17rem' }}>
                <Label htmlFor='details.tlsBrokerLocation.value'
                    className="text-xs text-gray-600">{translate.byKey(TranslationKeys.tlsBrokerServiceLocation)}:</Label>
                <Field
                    name="details.tlsBrokerLocation.value"
                    type="text"
                    className="flex flex-row items-center p-1 text-gray-800 text-sm bg-gray-input" />
            </div>
            <div className="mt-4 ml-2">
                <button
                    type='button'
                    className={"h-8 px-2 secondary bg-sidemenu-focus text-white  shadow-lg"}
                    onClick={onVerifyBrokerLocation}
                    id="testQueryButton">
                    <span className="mr-2">{translate.byKey(TranslationKeys.verify)}</span>
                </button>
            </div>
        </div>

        <div className="flex flex-col mt-6">
            <Label htmlFor='brokerHeading' className="text-portal-blue-primary-shade2 text-md font-semibold">{translate.byKey(TranslationKeys.certificateRevocationManagement)}</Label>
            <span className="text-sm text-gray-500">{translate.byKey(TranslationKeys.crlManagementTip)}</span>
        </div>

        <div className="flex flex-row mt-1 flex-wrap">
            <div className="flex flex-row mt-2 gap-2 flex-wrap">
                <Tooltip text={translate.byKey(TranslationKeys.updateCrlTip)} direction="bottom" show={true} contentPositionX='start'>
                    <button
                        type='button'
                        className={"h-8 px-3 secondary bg-sidemenu-focus text-white shadow-lg whitespace-nowrap min-w-fit"}
                        onClick={checkForCRLRefresh}
                        id="RefreshCrlFileButton">
                        {translate.byKey(TranslationKeys.refreshCrl)}
                    </button>
                </Tooltip>
                <Tooltip text={translate.byKey(TranslationKeys.importCrlTip)} direction="bottom" show={true} contentPositionX='start'>
                    <button
                        type='button'
                        className={"h-8 px-3 secondary bg-sidemenu-focus text-white shadow-lg whitespace-nowrap min-w-fit"}
                        onClick={() => setCrlOpenFile(true)}
                        id="importCrlButton">
                        {translate.byKey(TranslationKeys.importCrl)}
                    </button>
                </Tooltip>
                <Tooltip text={translate.byKey(TranslationKeys.removeCrlTip)} direction="bottom" show={true} contentPositionX='start'>
                    <button
                        type='button'
                        className={"h-8 px-3 warning secondary bg-sidemenu-focus text-white shadow-lg whitespace-nowrap min-w-fit"}
                        onClick={removeCrlFile}
                        id="removeCrlFileButton">
                        {translate.byKey(TranslationKeys.crlRemove)}
                    </button>
                </Tooltip>
            </div>
        </div>
        <ImportCrlFile isOpen={crlOpenFile} onClose={() => { setCrlOpenFile(false) }} setBusy={setBusy} />
    </div>
};
